import { useEffect, useReducer, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { useStyles } from "../../common/appStyles";
import { FormControl, FormHelperText, InputLabel, MenuItem, Paper, Select, TextField } from '@mui/material';
import { showSnackbar } from '../../services/snackbar.service';
import { IGrade, IRole } from '../../types/manage';
import { ISchoolDetail } from '../../types/schools';
import { GetAllGrades, GetRegionsForTenant, GetRoles, GetSchoolsForRegion, GetTenantList } from '../../services/user-service';
import { ITenantDetail, IRegionDetail } from '../../types/regions';
import { RoleEnum } from '../../types/enums';
import { BannerAd } from '../../components/main-layout/BannerAd';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

const initialState = {
    firstName: '',
    lastName: '',
    email: '',
    grade: null,
    school: '',
    schoolId: null,
    regions: [],
    roleId: null,
    needsAction: false,
    auth0UserId: '',
    isValid: {
        name: false,
        email: false,
    },
    touched: {
        name: false,
        email: false,
    },
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        case 'SET_VALID':
            return { ...state, isValid: { ...state.isValid, [action.field]: action.isValid } };
        case 'SET_TOUCHED':
            return { ...state, touched: { ...state.touched, [action.field]: true } };
        default:
            throw new Error();
    }
}

function isValidName(name) {
    return name.trim().length > 0;
}

function isValidEmail(email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

/**
 * @param props - all of the functions and properties from User Admin that
 *  need to be called or accessed on the User Detail Screen.
 * @returns - A fullscreen dialog which allows the user to either fill in fields to create a user or edit existing data in the fields to edit a user.
 * @Remarks This dialog is pulled up by the User Admin page. 
 * Drop down boxes pull data from the api as {@link IRole}, {@link ITenantDetail}, {@link IRegionDetail}, {@link IGrade} and {@link ISchoolDetail}.
 */
export function UserDetail(props) {
    const { showDialog, titleText, selectedUser, handleClose, handleSave, handleChangePassword } = props;
    const userId = selectedUser.id;
    const classes = useStyles();

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        email: selectedUser.email,
        grade: selectedUser.grade || null,
        school: selectedUser.school,
        schoolId: selectedUser.schoolId,
        userRegions: selectedUser.regions,
        roleId: selectedUser.roleId || initialState.roleId,
        roleName: selectedUser.roleName,
        needsAction: selectedUser.needsAction,
        auth0UserId: selectedUser.auth0UserId
    });

    const { firstName, lastName, email, grade, schoolId, userRegions, roleId, auth0UserId, isValid } = state;

    const [regions, setRegions] = useState<IRegionDetail[]>([]);
    const [schools, setSchools] = useState<ISchoolDetail[]>([]);
    const [roles, setRoles] = useState<IRole[]>([]);
    const [currentRegions, setCurrentRegions] = useState<String[]>([]);
    const [tenants, setTenants] = useState<ITenantDetail[]>([]);
    const [selectedTenant, setSelectedTenant] = useState<number | string>("");
    const [grades, setGrades] = useState<IGrade[]>([]);
    const user = useSelector((state: RootState) => state.userDetail);

    const getTenantList = () => {
        GetTenantList()
            .then(response => {
                setTenants(response);
            })
            .catch((error) => {
                showSnackbar('Error geting data', 'error', 5000);
            });
    }

    const getRegionList = (request) => {
        GetRegionsForTenant({ "id": request })
            .then(response => {
                setRegions(response);
            })
            .catch((error) => {
                showSnackbar('Error geting data', 'error', 5000);
            });
    }

    const getSchoolsForRegion = (req) => {
        GetSchoolsForRegion({ "regionId": req[0].id })
            .then(response => {
                setSchools(response);
            })
            .catch((error) => {
                showSnackbar('Error geting data', 'error', 5000);
            });
    }

    const getGrades = () => {
        GetAllGrades()
            .then(response => {
                setGrades(response);
            })
            .catch((error) => {
                showSnackbar('Error geting data', 'error', 5000);
            });
    }

    const getRoles = () => {
        GetRoles()
            .then(response => {
                setRoles(response);
            })
            .catch((error) => {
                showSnackbar('Error geting data', 'error', 5000);
            });
    }

    const parseRegionIds = (RegionList) => {
        return RegionList.map(region => region.id);
    }


    useEffect(() => {
        if (userId && userRegions.length) {
            const tenantId = userRegions[0]?.tenantId;
            getRegionList(tenantId);
            getSchoolsForRegion(userRegions);
            setSelectedTenant(tenantId);
        }
        getRoles();
        getGrades();
        getTenantList();
        if (selectedUser.firstName) {
            validateField('firstName', selectedUser.firstName);
        }
        if (selectedUser.lastName) {
            validateField('firstName', selectedUser.firstName);
        }
        if (selectedUser.email) {
            validateField('email', selectedUser.email);
        }
        if (currentRegions.length === 0) {
            setregions();
        }
    }, [selectedUser]);

    const setField = (field, value) => {
        dispatch({ type: 'SET_FIELD', field, value });
        validateField(field, value);
    };

    const setregions = () => {
        const regionList: string[] = [];
        userRegions.forEach(element => {
            regionList.push(JSON.stringify(element));
        });
        setCurrentRegions(regionList);
    };

    const validateField = (field, value) => {
        let isValid = false;
        switch (field) {
            case 'firstName':
                isValid = isValidName(value);
                break;
            case 'lastName':
                isValid = isValidName(value);
                break;
            case 'email':
                isValid = isValidEmail(value);
                break;
            default:
                break;
        }
        dispatch({ type: 'SET_VALID', field, isValid });
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();
        let updatedUser: any;
        if (roleId < 6) {
            setField('grade', null);
        }
        if (selectedUser.userId !== 0) {
            updatedUser = {
                'userId': userId,
                firstName,
                lastName,
                email,
                'currentGrade': grade,
                roleId,
                schoolId,
                "regionIdList": parseRegionIds(userRegions),
                auth0UserId
            };
            handleSave('update', updatedUser);
        }
        else {
            updatedUser = {
                firstName,
                lastName,
                email,
                "currentGrade": grade,
                roleId,
                schoolId,
                "regionIdList": parseRegionIds(userRegions)
            }
            handleSave('create', updatedUser);
        }
    };

    const handleResetPassword = (event: any) => {
        var request = email
        if (request !== selectedUser.email) {
            showSnackbar("Please Save changes to the User's Email first", "warning", 5000);
            return;
        }
        handleChangePassword(request);
    }

    const handleTenant = (event, value) => {
        setSelectedTenant(value);
        setField('userRegions', []);
        setField('schoolId', null);
        if (value) {
            getRegionList(value);
        }
    }

    const handleRegion = (event, value) => {
        if (value === null) {
            setField('userRegions', []);
            setCurrentRegions([]);
            return
        }
        if (Array.isArray(value)) {
            let parsed = value.map(item => {
                try {
                    return JSON.parse(item);
                } catch (error) {
                    return item;
                }
            });;
            setField('userRegions', parsed);
            setCurrentRegions(value);
        }
        else {
            value = JSON.parse(value);
            const valueArray = [value];
            setField('userRegions', valueArray);
            getSchoolsForRegion(valueArray);
        }
    }

    const handleRoleChange = (event, value) => {
        setField('roleId', value);
        handleRegion(event, null);
        handleTenant(event, null);
        setField('schoolId', null);
        setField('grade', null);
    }

    const isFormValid =
        (
            isValid.firstName &&
            isValidName(lastName) &&
            isValid.email &&
            (userRegions.length && roleId || roleId <= 2)
            &&
            ((!schoolId && roleId < 5) || (schoolId && roleId >= 5))
            &&
            ((!grade && roleId < 6) || (grade))
            &&
            ((selectedTenant) || roleId <= 2)
        );

    return (
        <div>
            <Dialog
                open={showDialog}
                onClose={handleClose}
                maxWidth={"lg"}
                fullScreen
            >
                <AppBar sx={{ position: 'fixed' }}>
                    <Toolbar>
                        <Typography sx={{ flex: 1 }} variant="h5" component="div">
                            {titleText}
                        </Typography>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Grid className={classes.wrapper} alignItems={"center"}>
                    <Grid
                        container
                        rowSpacing={6}
                        direction={"column"}
                        justifyContent={'center'}
                        alignItems={"center"}
                        className={classes.supWrapper}
                    >
                        {user.roleId >= RoleEnum.CredentialingManager && <BannerAd />}
                        <Grid container direction={"row"} columnSpacing={4} alignItems={"center"} justifyContent={"center"} className={classes.fixGridMargin}>
                            <Grid item xs={4}>
                                <Paper elevation={6}>
                                    <Grid
                                        container
                                        className={classes.gridPadding}
                                        direction={"column"}
                                        justifyContent={"flex-start"}
                                        rowSpacing={3}
                                    >
                                        <Grid item xs={6} md={6} className={classes.gridHeight}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="First Name"
                                                    value={firstName}
                                                    onChange={(event) => setField('firstName', event.target.value)}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6} md={6} className={classes.gridHeight}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="Last Name"
                                                    value={lastName}
                                                    onChange={(event) => setField('lastName', event.target.value)}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6} md={6} className={classes.gridHeight}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="Email"
                                                    value={email}
                                                    onChange={(event) => setField('email', event.target.value)}
                                                />
                                            </FormControl>
                                        </Grid>
                                        {roleId === RoleEnum.Youth ? (<Grid item xs={6} md={6} sx={{ paddingTop: .5 }} >
                                            <FormControl fullWidth>
                                                <InputLabel>Grade</InputLabel>
                                                <Select
                                                    MenuProps={{
                                                        disableScrollLock: true,
                                                        PaperProps: {
                                                            style: {
                                                                maxHeight: "15vh",
                                                            },
                                                        }
                                                    }}
                                                    labelId="grade-label"
                                                    label="Grade"
                                                    size='medium'
                                                    value={grade}
                                                    onChange={(event) => {
                                                        setField('grade', event.target.value);
                                                    }}
                                                >
                                                    {grades.map((grade) => (
                                                        <MenuItem key={grade.id} value={grade.id}>
                                                            {grade.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>) : <Grid sx={{ height: "8.5vh" }}></Grid>}
                                    </Grid>
                                </Paper>
                            </Grid>
                            <Grid item xs={4}>
                                <Paper elevation={6}>
                                    <Grid
                                        container
                                        direction={"row"}
                                        justifyContent="center"
                                        alignItems="center"
                                        className={classes.gridPadding}
                                        rowSpacing={3}
                                    >
                                        <Grid item xs={12} md={12}>
                                            <Grid>
                                                <FormControl fullWidth>
                                                    <InputLabel>Role</InputLabel>
                                                    <Select
                                                        labelId="role-label"
                                                        value={roleId}
                                                        label="Role"
                                                        onChange={(event) => {
                                                            handleRoleChange(event, event.target.value)
                                                        }}
                                                        MenuProps={{
                                                            disableScrollLock: true,
                                                            PaperProps: {
                                                                style: {
                                                                    maxHeight: "15vh",
                                                                },
                                                            }
                                                        }}
                                                    >
                                                        {roles.map((role) => (
                                                            <MenuItem value={role.id} key={roleId}>
                                                                {role.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <Grid>
                                                <FormControl fullWidth>
                                                    <InputLabel>Tenant</InputLabel>
                                                    <Select
                                                        labelId="tenant-label"
                                                        label="Tenant"
                                                        disabled={roleId <= RoleEnum.SystemAdministrator}
                                                        value={selectedTenant}
                                                        onChange={(event) => { handleTenant(event, event.target.value) }}
                                                        MenuProps={{
                                                            disableScrollLock: true,
                                                            PaperProps: {
                                                                style: {
                                                                    maxHeight: "15vh",
                                                                },
                                                            }
                                                        }}
                                                    >
                                                        {tenants.map((tenant) => (
                                                            <MenuItem key={tenant.id} value={tenant.id} aria-labelledby={tenant.name}>{tenant.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <Grid>
                                                <FormControl fullWidth>
                                                    <InputLabel>Region</InputLabel>
                                                    {roleId >= RoleEnum.SchoolCoordinator ? (
                                                        <Select
                                                            MenuProps={{
                                                                disableScrollLock: true,
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: "15vh",
                                                                    },
                                                                }
                                                            }}
                                                            labelId="region-label"
                                                            label="Region"
                                                            disabled={
                                                                (roleId === 0 ||
                                                                    (selectedTenant === null || selectedTenant === 0 || selectedTenant === undefined))
                                                            }
                                                            size='medium'
                                                            value={JSON.stringify(userRegions[0])}
                                                            onChange={(event) => { handleRegion(event, event.target.value); setField('schoolId', null); }}
                                                        >
                                                            {regions.map((reg) => (
                                                                <MenuItem key={reg.id} value={JSON.stringify(reg)} aria-labelledby={reg.name}>{reg.name}</MenuItem>
                                                            ))}
                                                        </Select>) : (
                                                        <Select
                                                            MenuProps={{
                                                                disableScrollLock: true,
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: "15vh",
                                                                    },
                                                                }
                                                            }}
                                                            labelId="region-label"
                                                            label="Region"
                                                            disabled={(!roleId || !selectedTenant) || roleId <= RoleEnum.SystemAdministrator}
                                                            multiple
                                                            value={currentRegions}
                                                            onChange={(event) => { handleRegion(event, event.target.value); setField('schoolId', null); }}
                                                        >
                                                            {regions.map((reg) => (
                                                                <MenuItem key={reg.id} value={JSON.stringify(reg)} aria-labelledby={reg.name}>{reg.name}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    )
                                                    }
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} md={12} >
                                            <Grid>
                                                {!(roleId >= RoleEnum.SchoolCoordinator) ? <Grid sx={{ height: "6vh" }}></Grid> : <FormControl fullWidth>
                                                    <InputLabel>School</InputLabel>
                                                    <Select
                                                        labelId="school-label"
                                                        label="School"
                                                        MenuProps={{
                                                            disableScrollLock: true,
                                                            PaperProps: {
                                                                style: {
                                                                    maxHeight: "15vh",
                                                                },
                                                            }
                                                        }}
                                                        disabled={!(userRegions.length === 0) ? false : true}
                                                        size='medium'
                                                        value={schoolId}
                                                        onChange={(event) => { setField('schoolId', event.target.value) }}
                                                    >
                                                        {schools.map((sch) => (
                                                            <MenuItem value={sch.id} key={sch.id}>
                                                                {sch.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={1.2} alignSelf={"center"} sx={{ minWidth: "20vh" }} >
                            <Grid container direction={"column"} rowSpacing={3}>
                                <Grid item xs={6} md={6}>
                                    <Button onClick={handleSubmit} variant="contained" disabled={!isFormValid} fullWidth>
                                        {userId > 0 ? "Update User" : "Create User"}
                                    </Button>
                                </Grid>
                                {userId > 0 &&
                                    <Grid item xs={6} md={6}>
                                        <Button onClick={handleResetPassword} variant="outlined" fullWidth>
                                            Reset Password
                                        </Button>
                                        {selectedUser.email !== email &&
                                            <FormHelperText>
                                                Save Email Changes first.
                                            </FormHelperText>
                                        }
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid >
            </Dialog >
        </div >
    );
}